import ipsum from "corporate-ipsum";
import { PropController } from "../../prop-controllers/base";
import { richTextV2DescendentsToData } from "./dto";
import { LinkPlugin } from "../../slate/LinkPlugin/linkPluginWithoutRenderElement";
import { InlinePlugin } from "../../slate/InlinePlugin";
import { TextAlignPlugin } from "../../slate/TextAlignPlugin";
import { BlockPlugin } from "../../slate/BlockPlugin";
import { TypographyPlugin } from "../../slate/TypographyPlugin";
import { InlineModePlugin } from "../../slate/InlineModePlugin";
const RichTextV2ControlType = "makeswift::controls::rich-text-v2";
const RichTextV2Mode = {
  Inline: "makeswift::controls::rich-text-v2::mode::inline",
  Block: "makeswift::controls::rich-text-v2::mode::block"
};
function RichText(config = {}) {
  return {
    type: RichTextV2ControlType,
    config: {
      mode: config.mode,
      defaultValue: config.defaultValue ?? (config?.mode === RichTextV2Mode.Inline ? "Edit this text" : ipsum(3)),
      plugins: config?.mode === RichTextV2Mode.Inline ? [InlineModePlugin()] : (
        // Note: this plugin references a `renderElement`-less LinkPlugin to prevent a circular dependency
        [BlockPlugin(), TypographyPlugin(), TextAlignPlugin(), InlinePlugin(), LinkPlugin()]
      )
    }
  };
}
RichText.Mode = RichTextV2Mode;
const RichTextV2ControlMessageType = {
  // COSMOS -> HOST
  RESET_VALUE: "makeswift::controls::rich-text-v2::control-message::reset-value",
  FOCUS: "makeswift::controls::rich-text-v2::control-message::focus",
  RUN_PLUGIN_CONTROL_ACTION: "makeswift::controls::rich-text-v2::control-message::run-plugin-control-action",
  // HOST -> COSMOS
  SET_DEFAULT_VALUE: "makeswift::controls::rich-text-v2::control-message::set-default-value",
  SET_PLUGIN_CONTROL_VALUE: "makeswift::controls::rich-text-v2::control-message::set-plugin-control-value",
  ON_CHANGE: "makeswift::controls::rich-text-v2::control-message::on-change",
  SELECT: "makeswift::controls::rich-text-v2::control-message::select",
  SWITCH_TO_BUILD_MODE: "makeswift::controls::rich-text-v2::control-message::switch-to-build-mode",
  CHANGE_BOX_MODEL: "makeswift::controls::rich-text-v2::control-message::change-box-model",
  REDO: "makeswift::controls::rich-text-v2::control-message::redo",
  UNDO: "makeswift::controls::rich-text-v2::control-message::undo"
};
class RichTextV2Control extends PropController {
  editor = null;
  defaultValue = null;
  descriptor;
  constructor(send, descriptor) {
    super(send);
    this.descriptor = descriptor;
    this.send = send;
  }
  recv = (message) => {
    if (!this.editor)
      return;
    switch (message.type) {
      case RichTextV2ControlMessageType.FOCUS: {
        this.editor.focusAndSelectAll();
        break;
      }
      case RichTextV2ControlMessageType.RESET_VALUE: {
        if (this.defaultValue) {
          this.editor.resetValue(this.defaultValue);
          setTimeout(() => {
            this.onLocalUserChange();
          });
        }
        break;
      }
      case RichTextV2ControlMessageType.RUN_PLUGIN_CONTROL_ACTION: {
        this.descriptor.config.plugins?.at(message.pluginIndex)?.control?.onChange(this.editor, message.value);
        break;
      }
    }
  };
  setEditor(editor) {
    this.editor = editor;
    this.send({
      type: RichTextV2ControlMessageType.SET_PLUGIN_CONTROL_VALUE,
      value: this.descriptor.config?.plugins?.map((plugin) => plugin?.control?.getValue(editor)) ?? []
    });
    const _onChange = editor.onChange;
    this.editor.onChange = (options) => {
      _onChange(options);
      this.updatePluginValues();
      if (this.editor == null || options?.operation == null)
        return;
      this.onLocalUserChange();
    };
  }
  setDefaultValue(defaultValue) {
    this.defaultValue = defaultValue;
    this.send({
      type: RichTextV2ControlMessageType.SET_DEFAULT_VALUE,
      value: defaultValue
    });
  }
  onLocalUserChange() {
    if (this.editor == null)
      return;
    this.send({
      type: RichTextV2ControlMessageType.ON_CHANGE,
      value: richTextV2DescendentsToData(this.editor.children, this.editor.currentKey)
    });
  }
  updatePluginValues() {
    const editor = this.editor;
    if (editor == null)
      return;
    this.send({
      type: RichTextV2ControlMessageType.SET_PLUGIN_CONTROL_VALUE,
      value: this.descriptor.config?.plugins?.map((plugin) => plugin?.control?.getValue(editor)) ?? []
    });
  }
  select() {
    this.send({ type: RichTextV2ControlMessageType.SELECT });
  }
  switchToBuildMode() {
    this.send({ type: RichTextV2ControlMessageType.SWITCH_TO_BUILD_MODE });
  }
  undo() {
    this.send({ type: RichTextV2ControlMessageType.UNDO });
  }
  redo() {
    this.send({ type: RichTextV2ControlMessageType.REDO });
  }
  changeBoxModel(boxModel) {
    this.send({ type: RichTextV2ControlMessageType.CHANGE_BOX_MODEL, payload: { boxModel } });
  }
}
function isRichTextV1Data(value) {
  return value !== void 0 && typeof value === "object" && !Array.isArray(value) && "document" in value;
}
export {
  RichText,
  RichTextV2Control,
  RichTextV2ControlMessageType,
  RichTextV2ControlType,
  RichTextV2Mode,
  isRichTextV1Data
};
//# sourceMappingURL=rich-text-v2.js.map